import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useSelector, useDispatch } from 'react-redux';
import { Redirect } from 'react-router-dom';

import ErrorPage from '../../components/ErrorPage';

import { setClubId } from '../../actions/club-actions';
import { setHeader } from '../../actions/header-actions';
import { getStandardErrorPageConfig } from '../../utils/errorPage';
import config from '../../../config';
import PlayersGroupProfilePage from './PlayersGroupProfilePage';
import { getNewClubPath, ClubTierTypes } from '../../utils/clubs';
import { showSplash } from '../../actions/splash-actions';

/**
 * The legacy club/college/high-school profile views were migrated to the new
 * Next.js app (utr-web-app). This wrapper now only exists to fetch the club's
 * tier type and decide between the two remaining outcomes:
 *   - Players Group clubs still render the legacy PlayersGroupProfilePage
 *     (not migrated yet).
 *   - Everything else redirects unconditionally to the new app.
 * The legacy ClubProfilePage and its component tree were removed.
 */
const ClubProfileWrapper = (props) => {
    const dispatch = useDispatch();
    const club = useSelector((state) => state.club);

    const getClub = () => {
        dispatch(setClubId(props.match.params.clubId, true));

        if (config.oldLogoClubIds?.length &&
            config.oldLogoClubIds.includes(parseInt(props.match.params.clubId))) {
            dispatch(setHeader({
                oldLogo: true,
            }));
        }
    };

    useEffect(() => {
        if (!club?.clubId) {
            getClub();
        }
    }, []);

    useEffect(() => {
        if (club?.clubId && club.clubId !== props.match.params.clubId) {
            getClub();
        }

        return () => {
            dispatch(setHeader({
                oldLogo: false,
            }));
        };
    }, [club?.clubId]);

    const clubTierTypeId = club?.clubDetails?.tierTypeId || club?.clubDetails?.tierType?.id;

    const inAppClubWrappers = {
        [ClubTierTypes.PERSONAL]: <PlayersGroupProfilePage {...props} />,
        [ClubTierTypes.CREATE_PLAY]: <Redirect to={'/'} />,
    };
    const isInAppClubType = clubTierTypeId in inAppClubWrappers;
    const detailsReady = !!(club?.clubDetailsFetched && club?.clubDetails);
    // Redirect only once details are fetched — before that the tier type is unknown and
    // a premature redirect would send every club (players groups included) to the new app.
    const shouldRedirectToNextApp = detailsReady && !isInAppClubType;

    // Redirect after commit, not during render, so location.replace truly replaces the current
    // /colleges/:id entry. Replacing during render pushed the Next.js URL on top instead, leaving
    // /colleges/:id behind — Back landed there, re-redirected forward, and took two presses to
    // get back to College Corner.
    useEffect(() => {
        if (shouldRedirectToNextApp && club?.clubId) {
            dispatch(showSplash(true));
            window.location.replace(`${config.nextAppHost}/${getNewClubPath(clubTierTypeId)}/${club.clubId}`);
        }
    }, [shouldRedirectToNextApp, club?.clubId, clubTierTypeId]);

    if (club?.clubDetailsFetched && !club?.clubDetails) {
        const errorConfig = getStandardErrorPageConfig({ errorData: club?.error, resourceName: 'club' });

        return (
            <ErrorPage
                subtitle={errorConfig.subtitle}
                showReloadButton={errorConfig.showReloadButton}
                showBackButton={errorConfig.showBackButton}
                data-testid="club-profile.error-page"
            />
        );
    }

    if (!club?.clubDetailsFetched || !club?.clubDetails) {
        return null;
    }

    // The redirect effect is navigating away; render nothing so the legacy page never mounts
    // (and never touches history) underneath the redirect. The splash overlay covers the screen.
    if (shouldRedirectToNextApp) {
        return null;
    }

    if (isInAppClubType) {
        return inAppClubWrappers[clubTierTypeId];
    }

    return null;
};

ClubProfileWrapper.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            clubId: PropTypes.string.isRequired,
        }),
    }).isRequired,
};

export default compose(ClubProfileWrapper);
